import { useEffect, useState } from 'react'

import type { GridApi } from 'ag-grid-community'

import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import {
  EMPTY_DEAL_GRAPH_DATA,
  getDisplayedListings,
  toDealGraphData,
  type DealGraphData,
} from './dealGraphData'

/**
 * Chart data for the rows the List View grid is currently showing, kept in step
 * with the grid.
 *
 * `modelUpdated` is the one event to listen to: the client-side row model
 * dispatches it after filtering, sorting, and any row-data replacement, so it
 * covers filter changes, sort changes, paging to a new page, and the Pusher bid
 * updates that flow in as fresh rows. There is also an initial read, because
 * the grid is already mounted and settled by the time the graph is toggled on —
 * no further `modelUpdated` would fire on its own.
 *
 * Requires `EventApiModule`; see the registration in `ListViewTable`.
 */
export const useDealGraphData = (
  gridApi: GridApi<CombinedListingSearchResult> | null
): DealGraphData => {
  const [dealGraphData, setDealGraphData] = useState<DealGraphData>(
    EMPTY_DEAL_GRAPH_DATA
  )

  useEffect(() => {
    if (!gridApi) {
      setDealGraphData(EMPTY_DEAL_GRAPH_DATA) // oxlint-disable-line react/set-state-in-effect
      return
    }

    const readGrid = () =>
      setDealGraphData(toDealGraphData(getDisplayedListings(gridApi)))

    readGrid()
    gridApi.addEventListener('modelUpdated', readGrid)

    return () => {
      // The grid outlives the graph normally, but not when the whole List View
      // unmounts — AG Grid warns if the API is touched after destruction.
      if (!gridApi.isDestroyed()) {
        gridApi.removeEventListener('modelUpdated', readGrid)
      }
    }
  }, [gridApi])

  return dealGraphData
}
